import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Alert,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  Spinner,
  InputGroup,
  Collapse,
} from "reactstrap";
import { toast } from "react-toastify";
import { useGetSuppliersQuery, useSendRfqEmailMutation } from "slices/InventoryManagement/inventoryManagementSlice";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import TagsInput from "react-tagsinput";
import "react-tagsinput/react-tagsinput.css";

interface Supplier {
  id: number;
  name: string;
  contact_email: string;
  contact_name?: string;
  contact_phone?: string;
}

interface SupplierEmailModalProps {
  isOpen: boolean;
  toggle: () => void;
  selectedSupplierIds?: number[];
  rfqItems: any[];
  purchaseRequestNumber: string;
  purchaseRequestId?: string | number;
}

const SupplierEmailModal: React.FC<SupplierEmailModalProps> = ({
  isOpen,
  toggle,
  selectedSupplierIds = [],
  rfqItems,
  purchaseRequestNumber,
  purchaseRequestId,
}) => {
  const pageSize = 50;
  const [page, setPage] = useState(1);
  const [allSuppliers, setAllSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierIdsState, setSelectedSupplierIdsState] = useState<Set<number>>(new Set(selectedSupplierIds));
  const [selectedSuppliers, setSelectedSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierForEmail, setSelectedSupplierForEmail] = useState<Supplier | null>(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailHistory, setEmailHistory] = useState<{ [supplierId: number]: boolean }>({});
  const [showSupplierSelection, setShowSupplierSelection] = useState(selectedSupplierIds.length === 0);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState("");
  const [ccEmails, setCcEmails] = useState<string>("");
  const [selectedSupplierEmails, setSelectedSupplierEmails] = useState<string[]>([]);
  const [attachmentFormat, setAttachmentFormat] = useState<"excel" | "csv">("excel");
  const [extraAttachment, setExtraAttachment] = useState<File | null>(null);
  const [showRfqItems, setShowRfqItems] = useState(true);
  const editorRef = useRef<any>(null);
  // Per-supplier email data storage
  const [supplierEmailData, setSupplierEmailData] = useState<{ [supplierId: number]: { subject: string; body: string; ccEmails: string } }>({});

  const { data: suppliersData, isLoading: loadingSuppliers } = useGetSuppliersQuery(
    { page, pageSize, search: supplierSearchQuery },
    { skip: !isOpen }
  );
  const [sendRfqEmail, { isLoading: isSendingEmailAPI }] = useSendRfqEmailMutation();

  // Load all suppliers from paginated response
  useEffect(() => {
    if (!suppliersData) {
      setAllSuppliers([]);
      return;
    }

    const raw =
      Array.isArray(suppliersData)
        ? suppliersData
        : suppliersData.results || suppliersData.suppliers || [];

    const suppliersWithEmail = raw.filter(
      (supplier: Supplier) => supplier.contact_email
    );
    setAllSuppliers(suppliersWithEmail);
  }, [suppliersData]);

  // Update selected suppliers when selection changes
  useEffect(() => {
    if (allSuppliers.length > 0 && selectedSupplierIdsState.size > 0) {
      const filtered = allSuppliers.filter((supplier: Supplier) =>
        selectedSupplierIdsState.has(supplier.id)
      );
      setSelectedSuppliers(filtered);
      // Set CC emails and selected supplier emails
      const emails = filtered.map((s) => s.contact_email).join(", ");
      setCcEmails(emails);
      setSelectedSupplierEmails(filtered.map((s) => s.contact_email));
      setShowSupplierSelection(false);
      // Auto-select first supplier if none selected
      if (!selectedSupplierForEmail && filtered.length > 0) {
        setSelectedSupplierForEmail(filtered[0]);
      }
    } else if (selectedSupplierIds.length > 0) {
      // Use initial selectedSupplierIds if provided
      const filtered = allSuppliers.filter((supplier: Supplier) =>
        selectedSupplierIds.includes(supplier.id)
      );
      setSelectedSuppliers(filtered);
      setSelectedSupplierIdsState(new Set(selectedSupplierIds));
      // Set CC emails and selected supplier emails
      const emails = filtered.map((s) => s.contact_email).join(", ");
      setCcEmails(emails);
      setSelectedSupplierEmails(filtered.map((s) => s.contact_email));
      setShowSupplierSelection(false);
      // Auto-select first supplier if none selected
      if (!selectedSupplierForEmail && filtered.length > 0) {
        setSelectedSupplierForEmail(filtered[0]);
      }
    }
  }, [allSuppliers, selectedSupplierIdsState, selectedSupplierIds]);

  const handleToggleSupplierSelection = (supplierId: number) => {
    setSelectedSupplierIdsState((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(supplierId)) {
        newSet.delete(supplierId);
      } else {
        newSet.add(supplierId);
      }
      return newSet;
    });
  };

  const handleSelectAllSuppliers = () => {
    if (selectedSupplierIdsState.size === allSuppliers.length) {
      setSelectedSupplierIdsState(new Set());
    } else {
      setSelectedSupplierIdsState(new Set(allSuppliers.map((s) => s.id)));
    }
  };

  const handleProceedToEmail = () => {
    if (selectedSupplierIdsState.size === 0) {
      toast.error("Please select at least one supplier");
      return;
    }
    // Update selected suppliers based on current selection
    const filtered = allSuppliers.filter((supplier: Supplier) =>
      selectedSupplierIdsState.has(supplier.id)
    );
    setSelectedSuppliers(filtered);
    // Set CC emails and selected supplier emails
    const emails = filtered.map((s) => s.contact_email).join(", ");
    setCcEmails(emails);
    setSelectedSupplierEmails(filtered.map((s) => s.contact_email));
    setShowSupplierSelection(false);
  };

  // Filter suppliers based on search query
  const filteredSuppliers = useMemo(() => {
    if (!supplierSearchQuery.trim()) {
      return selectedSuppliers;
    }
    const query = supplierSearchQuery.toLowerCase();
    return selectedSuppliers.filter(
      (supplier) =>
        supplier.name.toLowerCase().includes(query) ||
        supplier.contact_email.toLowerCase().includes(query) ||
        (supplier.contact_name && supplier.contact_name.toLowerCase().includes(query))
    );
  }, [selectedSuppliers, supplierSearchQuery]);

  // Filter all suppliers for selection step
  const filteredAllSuppliers = useMemo(() => {
    if (!supplierSearchQuery.trim()) {
      return allSuppliers;
    }
    const query = supplierSearchQuery.toLowerCase();
    return allSuppliers.filter(
      (supplier) =>
        supplier.name.toLowerCase().includes(query) ||
        supplier.contact_email.toLowerCase().includes(query) ||
        (supplier.contact_name && supplier.contact_name.toLowerCase().includes(query))
    );
  }, [allSuppliers, supplierSearchQuery]);

  // Generate default email subject and body when supplier is selected
  useEffect(() => {
    if (selectedSupplierForEmail && rfqItems.length > 0 && !showSupplierSelection) {
      const supplierId = selectedSupplierForEmail.id;
      
      // Check if we have saved data for this supplier
      if (supplierEmailData[supplierId]) {
        setEmailSubject(supplierEmailData[supplierId].subject);
        setEmailBody(supplierEmailData[supplierId].body);
        setCcEmails(supplierEmailData[supplierId].ccEmails);
      } else {
        // Default subject
        const defaultSubject = `RFQ - Quotation Request - ${purchaseRequestNumber}`;
        setEmailSubject(defaultSubject);
        
        // Default body
        const defaultBody = `<p>We request you to provide your competitive quotation for the above products.</p>

<p><strong>Please include in your quotation:</strong></p>
<ul>
  <li>Unit price for each product</li>
  <li>Total amount for the requested quantities</li>
  <li>Delivery timeline and lead time</li>
  <li>Any applicable taxes and additional charges</li>
  <li>Payment terms and conditions</li>
  <li>Quotation validity period</li>
  <li>Your company details and contact information</li>
</ul>

<p>We look forward to your prompt response and competitive pricing.</p>

<p>Thank you for your business!</p>`;

        setEmailBody(defaultBody);
        setCcEmails(selectedSupplierForEmail.contact_email);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupplierForEmail?.id, rfqItems.length, purchaseRequestNumber, showSupplierSelection]);

  const handleRemoveSupplierEmail = (emailToRemove: string) => {
    setSelectedSupplierEmails((prev) => prev.filter((email) => email !== emailToRemove));
    // Update CC emails
    const updated = selectedSupplierEmails.filter((email) => email !== emailToRemove);
    setCcEmails(updated.join(", "));
  };

  const handleCcEmailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCcEmails(value);
    // Parse emails from comma-separated string
    const emails = value.split(",").map((email) => email.trim()).filter((email) => email);
    setSelectedSupplierEmails(emails);
  };

  // Handle selecting a supplier for email composition
  const handleSelectSupplierForEmail = (supplier: Supplier) => {
    // Save current supplier's email data before switching
    if (selectedSupplierForEmail) {
      setSupplierEmailData((prev) => ({
        ...prev,
        [selectedSupplierForEmail.id]: {
          subject: emailSubject,
          body: emailBody,
          ccEmails: ccEmails,
        },
      }));
    }
    
    // Select new supplier
    setSelectedSupplierForEmail(supplier);
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadRfqFile = async () => {
    if (!purchaseRequestId) return;
    setIsDownloading(true);
    try {
      const token = sessionStorage.getItem("authUser") || localStorage.getItem("authUser");
      const parsed = token ? JSON.parse(token) : null;
      const accessToken = parsed?.token || parsed?.access || "";
      const itemParams = rfqItems.map((item: any) => `&item_ids=${item.id}`).join("");
      const url = `${(window as any).__RUNTIME_CONFIG__?.REACT_APP_API_URL || ""}inventory/api/v1/purchase-requests/${purchaseRequestId}/download-rfq-file/?file_format=${attachmentFormat}${itemParams}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const ext = attachmentFormat === "excel" ? "xlsx" : "csv";
      const filename = `RFQ_file.${ext}`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error("Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle sending email to current supplier
  const handleSendEmail = async () => {
    if (!selectedSupplierForEmail) {
      toast.error("Please select a supplier");
      return;
    }

    if (!emailSubject.trim()) {
      toast.error("Please enter email subject");
      return;
    }

    // Check if email body has actual content (not just HTML tags/whitespace)
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = emailBody;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    if (!textContent.trim()) {
      toast.error("Please enter email body");
      return;
    }

    setIsSendingEmail(true);

    try {
      if (!purchaseRequestId) {
        toast.error("Purchase Request ID is missing");
        setIsSendingEmail(false);
        return;
      }

      // Prepare email body with RFQ table
      const rfqTableHTML = generateRfqTableHTML();
      const fullEmailBody = rfqTableHTML + emailBody;

      // Call the actual API
      const result = await sendRfqEmail({
        purchase_request_id: purchaseRequestId,
        supplier_ids: [selectedSupplierForEmail.id],
        subject: emailSubject,
        body: fullEmailBody,
        cc_emails: ccEmails.split(",").map(e => e.trim()).filter(e => e),
        attachment_format: attachmentFormat,
        selected_item_ids: rfqItems.map((item: any) => item.id),
        extra_attachment: extraAttachment || undefined,
      }).unwrap();

      // Show success message
      const successMessage = result.message || `Email sent successfully to ${selectedSupplierForEmail.name}`;
      toast.success(successMessage, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Mark as sent
      setEmailHistory((prev) => ({
        ...prev,
        [selectedSupplierForEmail.id]: true,
      }));

      // Find next unsent supplier
      const unsentSuppliers = selectedSuppliers.filter(
        (s) => !emailHistory[s.id]
      );
      
      if (unsentSuppliers.length > 0) {
        // Move to next supplier
        const nextSupplier = unsentSuppliers[0];
        setSelectedSupplierForEmail(nextSupplier);
        // Clear form for next supplier (will be populated by useEffect)
        setEmailSubject("");
        setEmailBody("");
        setCcEmails(nextSupplier.contact_email);
        
        // Show info message about next supplier
        toast.info(`Moving to next supplier: ${nextSupplier.name}`, {
          position: "top-right",
          autoClose: 2000,
        });
      } else {
        // All emails sent
        toast.success("All emails have been sent successfully!", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error: any) {
      console.error("Error sending email:", error);
      const errorMessage = error?.data?.error || error?.data?.message || error?.message || "Failed to send email. Please try again.";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Get next unsent supplier
  const getNextUnsentSupplier = () => {
    const unsentSuppliers = selectedSuppliers.filter(
      (s) => !emailHistory[s.id]
    );
    return unsentSuppliers.length > 0 ? unsentSuppliers[0] : null;
  };

  // Generate RFQ table HTML
  const generateRfqTableHTML = () => {
    if (rfqItems.length === 0) return "";

    const tableRows = rfqItems
      .map(
        (item, index) => `
        <tr>
          <td style="padding: 10px; text-align: center; border: 1px solid #dee2e6; background-color: #ffffff;">${index + 1}</td>
          <td style="padding: 10px; border: 1px solid #dee2e6; background-color: #ffffff;">
            ${item.product_name || "N/A"}
          </td>
          <td style="padding: 10px; text-align: center; border: 1px solid #dee2e6; background-color: #ffffff;">
            ${item.quantity ? parseFloat(item.quantity).toFixed(2) : "0.00"}
          </td>
          <td style="padding: 10px; text-align: center; border: 1px solid #dee2e6; background-color: #ffffff;">
            ${item.measurement_name || "-"}
          </td>
          <td style="padding: 10px; text-align: center; border: 1px solid #dee2e6; background-color: #ffffff;">
            ${item.expected_delivery_date
              ? new Date(item.expected_delivery_date).toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })
              : "Not specified"}
          </td>
          <td style="padding: 10px; border: 1px solid #dee2e6; background-color: #ffffff;">
            ${item.delivery_note || "-"}
          </td>
          <td style="padding: 10px; border: 1px solid #dee2e6; background-color: #ffffff;">
            ${item.pr_model_number || "-"}
          </td>
        </tr>`
      )
      .join("");

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #0d6efd; padding-bottom: 15px;">
          <h2 style="color: #0d6efd; margin: 0; font-size: 24px;">RFQ ITEMS LIST - QUOTATION REQUEST</h2>
        </div>
        <div style="margin-bottom: 20px;">
          <p style="margin: 5px 0;"><strong>Purchase Request:</strong> ${purchaseRequestNumber}</p>
          <p style="margin: 5px 0;"><strong>Date:</strong> ${new Date().toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })}</p>
          <p style="margin: 5px 0;"><strong>Total Selected Items:</strong> ${rfqItems.length}</p>
        </div>
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #dee2e6; font-family: Arial, sans-serif; font-size: 14px;">
          <thead>
            <tr style="background-color: #0d6efd; color: #ffffff;">
              <th style="padding: 12px; text-align: center; border: 1px solid #0d6efd; font-weight: bold;">No.</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #0d6efd; font-weight: bold;">Product Name</th>
              <th style="padding: 12px; text-align: center; border: 1px solid #0d6efd; font-weight: bold;">Quantity</th>
              <th style="padding: 12px; text-align: center; border: 1px solid #0d6efd; font-weight: bold;">Unit</th>
              <th style="padding: 12px; text-align: center; border: 1px solid #0d6efd; font-weight: bold;">Expected Delivery</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #0d6efd; font-weight: bold;">Delivery Note</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #0d6efd; font-weight: bold;">PR Model Number</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        <div style="background-color: #fff3cd; border: 1px solid #ffc107; padding: 15px; margin: 20px 0; border-radius: 4px;">
          <p style="margin: 0; font-weight: bold; color: #000000; font-size: 16px;">
            IMPORTANT: PLEASE QUOTE THE AMOUNT FOR THE PRODUCTS
          </p>
        </div>
      </div>`;
  };

  const handleClose = () => {
    // Save current supplier's data before closing
    if (selectedSupplierForEmail) {
      setSupplierEmailData((prev) => ({
        ...prev,
        [selectedSupplierForEmail.id]: {
          subject: emailSubject,
          body: emailBody,
          ccEmails: ccEmails,
        },
      }));
    }
    setEmailSubject("");
    setEmailBody("");
    setSupplierSearchQuery("");
    setCcEmails("");
    setSelectedSupplierEmails([]);
    setSelectedSupplierForEmail(null);
    setIsSendingEmail(false);
    toggle();
  };

  if (loadingSuppliers) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} size="xl">
        <ModalBody className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-3">Loading suppliers...</p>
        </ModalBody>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl" style={{ maxWidth: "95vw", width: "1400px" }}>
      <ModalHeader toggle={handleClose} className="bg-light py-3">
        <div className="d-flex align-items-center">
          <i className="ri-mail-send-line text-primary me-2 fs-5"></i>
          <div>
            <h5 className="modal-title mb-0 fw-bold">
              {showSupplierSelection ? "Select Suppliers" : "Email Template"}
            </h5>
            <small className="text-muted">
              {showSupplierSelection
                ? "Select suppliers to send RFQ emails"
                : "Compose and send RFQ email to selected suppliers"}
            </small>
          </div>
        </div>
        {!showSupplierSelection && (
          <Badge color="primary" className="ms-2 fs-6">
            {selectedSuppliers.length} Suppliers
          </Badge>
        )}
      </ModalHeader>

      <ModalBody className="p-0">
        {showSupplierSelection ? (
          <div className="p-4">
            <Alert color="info" className="mb-4">
              <h6 className="alert-heading">
                <i className="ri-information-line me-2"></i>
                Step 1: Select Suppliers
              </h6>
              <p className="mb-0">
                Select one or more suppliers to send RFQ emails. You can customize
                the email subject and body for each supplier individually.
              </p>
            </Alert>

            <div className="mb-3 d-flex justify-content-between align-items-center px-4">
              <FormGroup check className="mb-0">
                <Input
                  type="checkbox"
                  id="selectAllSuppliers"
                  checked={
                    filteredAllSuppliers.length > 0 &&
                    filteredAllSuppliers.every((s) => selectedSupplierIdsState.has(s.id))
                  }
                  onChange={handleSelectAllSuppliers}
                />
                <Label for="selectAllSuppliers" check className="fw-semibold ms-2">
                  Select All {supplierSearchQuery ? `(${filteredAllSuppliers.length})` : ""}
                </Label>
              </FormGroup>
              <Badge color="primary" className="fs-6">
                {selectedSupplierIdsState.size} selected
              </Badge>
            </div>

            {/* Search Input for Supplier Selection */}
            <div className="mb-3 px-4">
              <InputGroup>
                <Input
                  type="text"
                  placeholder="Search suppliers..."
                  value={supplierSearchQuery}
                  onChange={(e) => {
                    setSupplierSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  className="form-control"
                />
                <Button color="light" disabled>
                  <i className="ri-search-line"></i>
                </Button>
              </InputGroup>
              {supplierSearchQuery && (
                <small className="text-muted mt-2 d-block">
                  Showing {filteredAllSuppliers.length} supplier(s)
                </small>
              )}
            </div>

            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              {allSuppliers.length === 0 ? (
                <div className="text-center py-5">
                  <i className="ri-inbox-line display-5 text-muted opacity-50 mb-3"></i>
                  <p className="text-muted">No suppliers with email addresses found</p>
                </div>
              ) : filteredAllSuppliers.length === 0 ? (
                <div className="text-center py-5">
                  <i className="ri-search-line display-5 text-muted opacity-50 mb-3"></i>
                  <p className="text-muted">No suppliers found matching "{supplierSearchQuery}"</p>
                </div>
              ) : (
                <div>
                  {filteredAllSuppliers.map((supplier) => (
                    <Card
                      key={supplier.id}
                      className={`mb-2 ${
                        selectedSupplierIdsState.has(supplier.id)
                          ? "border-primary shadow-sm"
                          : ""
                      }`}
                    >
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center">
                          <FormGroup check className="mb-0 me-3">
                            <Input
                              type="checkbox"
                              checked={selectedSupplierIdsState.has(supplier.id)}
                              onChange={() => handleToggleSupplierSelection(supplier.id)}
                            />
                          </FormGroup>
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-semibold">{supplier.name}</h6>
                            <small className="text-muted d-block">
                              <i className="ri-mail-line me-1"></i>
                              {supplier.contact_email}
                            </small>
                            {supplier.contact_name && (
                              <small className="text-muted d-block mt-1">
                                <i className="ri-user-line me-1"></i>
                                {supplier.contact_name}
                              </small>
                            )}
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              )}
            </div>
            {suppliersData && !Array.isArray(suppliersData) && (
              <div className="px-4 pt-3 d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Page {suppliersData.current_page} of {suppliersData.total_page}
                </small>
                <div>
                  <Button
                    color="light"
                    size="sm"
                    className="me-2"
                    disabled={suppliersData.current_page <= 1 || loadingSuppliers}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    disabled={
                      suppliersData.current_page >= suppliersData.total_page || loadingSuppliers
                    }
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="row g-0">
            {/* Left Panel - Supplier List */}
            <div className="col-md-4 border-end">
              <div className="p-3 border-bottom bg-light">
                <h6 className="mb-0 fw-semibold">
                  <i className="ri-group-line me-2"></i>
                  Selected Suppliers
                </h6>
                <small className="text-muted">
                  {selectedSuppliers.length} supplier(s) • {selectedSuppliers.filter(s => emailHistory[s.id]).length} sent
                </small>
              </div>
              {/* Search Input */}
              <div className="p-3 border-bottom">
                <InputGroup>
                  <Input
                    type="text"
                    placeholder="Search suppliers..."
                    value={supplierSearchQuery}
                    onChange={(e) => {
                      setSupplierSearchQuery(e.target.value);
                      setPage(1);
                    }}
                    className="form-control"
                  />
                  <Button color="light" disabled>
                    <i className="ri-search-line"></i>
                  </Button>
                </InputGroup>
              </div>
              <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                {filteredSuppliers.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="ri-search-line display-5 text-muted opacity-50 mb-3"></i>
                    <p className="text-muted">No suppliers found</p>
                  </div>
                ) : (
                  <div className="p-2">
                    {filteredSuppliers.map((supplier) => (
                      <Card
                        key={supplier.id}
                        className={`mb-2 cursor-pointer ${
                          selectedSupplierForEmail?.id === supplier.id
                            ? "border-primary shadow-sm"
                            : ""
                        }`}
                        onClick={() => handleSelectSupplierForEmail(supplier)}
                        style={{ cursor: "pointer" }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-semibold">
                                {supplier.name}
                              </h6>
                              <small className="text-muted d-block">
                                <i className="ri-mail-line me-1"></i>
                                {supplier.contact_email}
                              </small>
                              {supplier.contact_name && (
                                <small className="text-muted d-block mt-1">
                                  <i className="ri-user-line me-1"></i>
                                  {supplier.contact_name}
                                </small>
                              )}
                            </div>
                            {emailHistory[supplier.id] && (
                              <Badge color="success" className="ms-2">
                                <i className="ri-check-line"></i>
                              </Badge>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Email Form */}
            <div className="col-md-8">
              {selectedSupplierForEmail ? (
                <div className="p-4">
                  <Alert color="info" className="mb-4">
                    <h6 className="alert-heading">
                      <i className="ri-information-line me-2"></i>
                      Composing Email for {selectedSupplierForEmail.name}
                    </h6>
                    <p className="mb-0 small">
                      Customize the subject and body for this supplier. You can send emails one by one.
                    </p>
                  </Alert>

                  <h5 className="mb-4 fw-bold">Email Template</h5>

              {/* Subject */}
              <FormGroup>
                <Label for="emailSubject" className="fw-semibold">
                  Subject <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="emailSubject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="RFQ - Quotation Request - PR_XXX"
                  className="form-control"
                />
              </FormGroup>

              {/* CC Emails */}
              <FormGroup>
                <Label for="ccEmails" className="fw-semibold">
                  CC Emails (comma-separated)
                </Label>
                <Input
                  type="textarea"
                  id="ccEmails"
                  rows={2}
                  value={ccEmails}
                  onChange={(e) => setCcEmails(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                  className="form-control"
                />
                <small className="text-muted mt-1 d-block">
                  To: <strong>{selectedSupplierForEmail.contact_email}</strong>
                </small>
              </FormGroup>

              {/* Attachment Format */}
              <FormGroup>
                <Label className="fw-semibold d-block mb-2">Attachment Format</Label>
                <div className="d-flex gap-4">
                  <FormGroup check>
                    <Input
                      type="radio"
                      name="attachmentFormat"
                      id="excelFormat"
                      checked={attachmentFormat === "excel"}
                      onChange={() => setAttachmentFormat("excel")}
                    />
                    <Label for="excelFormat" check className="ms-2">
                      Excel (.xlsx)
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Input
                      type="radio"
                      name="attachmentFormat"
                      id="csvFormat"
                      checked={attachmentFormat === "csv"}
                      onChange={() => setAttachmentFormat("csv")}
                    />
                    <Label for="csvFormat" check className="ms-2">
                      CSV (.csv)
                    </Label>
                  </FormGroup>
                </div>
              </FormGroup>

              {/* Download Generated File */}
              {purchaseRequestId && (
                <FormGroup>
                  <Label className="fw-semibold d-block mb-2">Download Generated File</Label>
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    onClick={handleDownloadRfqFile}
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <><Spinner size="sm" className="me-1" /> Downloading...</>
                    ) : (
                      <><i className="ri-download-2-line me-1"></i>
                      Download {attachmentFormat === "excel" ? "Excel (.xlsx)" : "CSV (.csv)"}</>
                    )}
                  </Button>
                </FormGroup>
              )}

              {/* Additional Attachment */}
              <FormGroup>
                <Label className="fw-semibold d-block mb-2">Additional Attachment (Optional)</Label>
                <Input
                  type="file"
                  onChange={(e) => setExtraAttachment(e.target.files?.[0] || null)}
                />
                {extraAttachment && (
                  <div className="mt-1 d-flex align-items-center gap-2">
                    <small className="text-muted">{extraAttachment.name}</small>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0"
                      onClick={() => setExtraAttachment(null)}
                    >
                      <i className="ri-close-line"></i> Remove
                    </button>
                  </div>
                )}
              </FormGroup>

              {/* RFQ Items Information (Read-only) - Collapsible */}
              {rfqItems.length > 0 && (
                <FormGroup>
                  <div className="border rounded">
                    <div
                      className="d-flex justify-content-between align-items-center p-3 bg-light cursor-pointer"
                      onClick={() => setShowRfqItems(!showRfqItems)}
                      style={{ cursor: "pointer" }}
                    >
                      <Label className="fw-semibold mb-0">
                        RFQ Items Information (Read-only)
                      </Label>
                      <i
                        className={`ri-arrow-${showRfqItems ? "up" : "down"}-s-line`}
                      ></i>
                    </div>
                    <Collapse isOpen={showRfqItems}>
                      <div className="p-3">
                        <div className="text-center mb-3 pb-3 border-bottom">
                          <h4 className="text-primary mb-2 fw-bold">
                            RFQ ITEMS LIST - QUOTATION REQUEST
                          </h4>
                        </div>
                        <div className="mb-3">
                          <p className="mb-1">
                            <strong>Purchase Request:</strong> {purchaseRequestNumber}
                          </p>
                          <p className="mb-1">
                            <strong>Date:</strong>{" "}
                            {new Date().toLocaleDateString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                            })}
                          </p>
                          <p className="mb-0">
                            <strong>Total Selected Items:</strong> {rfqItems.length}
                          </p>
                        </div>
                        <div className="table-responsive">
                          <Table bordered className="mb-0">
                            <thead>
                              <tr style={{ backgroundColor: "#0d6efd", color: "#ffffff" }}>
                                <th className="text-center" style={{ padding: "12px" }}>
                                  No.
                                </th>
                                <th style={{ padding: "12px" }}>Product Name</th>
                                <th className="text-center" style={{ padding: "12px" }}>
                                  Quantity
                                </th>
                                <th className="text-center" style={{ padding: "12px" }}>
                                  Unit
                                </th>
                                <th className="text-center" style={{ padding: "12px" }}>
                                  Expected Delivery
                                </th>
                                <th style={{ padding: "12px" }}>Delivery Note</th>
                                <th style={{ padding: "12px" }}>PR Model Number</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rfqItems.map((item, index) => (
                                <tr key={item.id}>
                                  <td className="text-center">{index + 1}</td>
                                  <td>{item.product_name || "N/A"}</td>
                                  <td className="text-center">
                                    {item.quantity
                                      ? parseFloat(item.quantity).toFixed(2)
                                      : "0.00"}
                                  </td>
                                  <td className="text-center">
                                    {item.measurement_name || "-"}
                                  </td>
                                  <td className="text-center">
                                    {item.expected_delivery_date
                                      ? new Date(
                                          item.expected_delivery_date
                                        ).toLocaleDateString("en-US", {
                                          month: "2-digit",
                                          day: "2-digit",
                                          year: "numeric",
                                        })
                                      : "Not specified"}
                                  </td>
                                  <td>{item.delivery_note || "-"}</td>
                                  <td>{item.pr_model_number || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Collapse>
                  </div>
                </FormGroup>
              )}

              {/* Email Body Content (Editable) */}
              <FormGroup>
                <Label for="emailBody" className="fw-semibold">
                  Email Body Content <span className="text-danger">*</span>{" "}
                  <span className="text-muted">(Editable)</span>
                </Label>
                <div className="ckeditor-wrapper border rounded p-2" style={{ minHeight: "300px" }}>
                  {selectedSupplierForEmail && (
                    <CKEditor
                      editor={ClassicEditor as any}
                      data={emailBody}
                      onReady={(editor: any) => {
                        if (editor) {
                          editorRef.current = editor;
                        }
                      }}
                      onChange={(event: any, editor: any) => {
                        try {
                          if (editor) {
                            const data = editor.getData();
                            setEmailBody(data);
                          }
                        } catch (error) {
                          console.error("CKEditor onChange error:", error);
                        }
                      }}
                      config={{
                        toolbar: [
                          "heading",
                          "|",
                          "bold",
                          "italic",
                          "underline",
                          "link",
                          "bulletedList",
                          "numberedList",
                          "|",
                          "blockQuote",
                          "codeBlock",
                          "insertTable",
                          "|",
                          "undo",
                          "redo",
                        ],
                      }}
                    />
                  )}
                </div>
              </FormGroup>

              {/* Attachment Confirmation Banner */}
              {rfqItems.length > 0 && (
                <Alert color="info" className="mb-0">
                  <i className="ri-attachment-line me-2"></i>
                  Selected Items: {rfqItems.length} item(s) will be included in the
                  attachment.
                </Alert>
              )}
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="ri-mail-send-line display-4 text-muted opacity-50 mb-3"></i>
                  <h5 className="text-muted mb-2">Select a Supplier</h5>
                  <p className="text-muted">
                    Choose a supplier from the list to compose and send an email
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="bg-light">
        <div className="d-flex justify-content-end w-100 align-items-center gap-2">
          <Button color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          {!showSupplierSelection && selectedSupplierForEmail && (
            <>
              {getNextUnsentSupplier() && getNextUnsentSupplier()?.id !== selectedSupplierForEmail.id && (
                <Button
                  color="info"
                  onClick={() => {
                    // Save current supplier's data
                    setSupplierEmailData((prev) => ({
                      ...prev,
                      [selectedSupplierForEmail.id]: {
                        subject: emailSubject,
                        body: emailBody,
                        ccEmails: ccEmails,
                      },
                    }));
                    // Move to next unsent supplier
                    const nextSupplier = getNextUnsentSupplier();
                    if (nextSupplier) {
                      setSelectedSupplierForEmail(nextSupplier);
                    }
                  }}
                  disabled={isSendingEmail}
                >
                  <i className="ri-arrow-right-line me-2"></i>
                  Next Supplier
                </Button>
              )}
              <Button
                color="success"
                onClick={handleSendEmail}
                disabled={
                  isSendingEmail ||
                  !emailSubject.trim() ||
                  !emailBody.trim() ||
                  !selectedSupplierForEmail ||
                  emailHistory[selectedSupplierForEmail.id]
                }
              >
              {(isSendingEmail || isSendingEmailAPI) ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Sending...
                </>
              ) : emailHistory[selectedSupplierForEmail.id] ? (
                <>
                  <i className="ri-check-line me-2"></i>
                  Already Sent
                </>
              ) : (
                <>
                  <i className="ri-mail-send-line me-2"></i>
                  Send Email to {selectedSupplierForEmail.name}
                </>
              )}
              </Button>
            </>
          )}
          {showSupplierSelection && (
            <Button color="primary" onClick={handleProceedToEmail}>
              Proceed to Email
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default SupplierEmailModal;
